import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Clock,
  Target
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import assignmentRuleService from '../../../api/crm/assignment-rules/assignmentRuleService';
import { API_BASE_URL } from '../../../api/config';
import { toast } from 'react-hot-toast';
import { formatFieldName, formatOperatorName, formatConditionValue } from '../../../utils/stringUtils';
const AssignmentRuleDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return 'High';
      case 2: return 'Medium';
      case 3: return 'Low';
      default: return `Priority ${priority}`;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'bg-red-100 text-red-800';
      case 2: return 'bg-yellow-100 text-yellow-800';
      case 3: return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  // Load user data for assignee IDs
  const loadUsersByIds = useCallback(async (assigneeIds) => {
    if (!assigneeIds || assigneeIds.length === 0) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/training-assignments/users?limit=100`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.responseCode === 200) {
        const usersData = result.data?.users || [];
        const assigneeUsers = usersData.filter(user => assigneeIds.includes(user._id));
        
        const transformedUsers = assigneeUsers.map(user => ({
          id: user._id,
          name: `${user.firstName} ${user.lastName}`.trim(),
          email: user.email,
          department: user.department,
          role: user.role
        }));

        setUsers(transformedUsers);
        return transformedUsers;
      }
    } catch (error) {
    }
  }, []);

  // Fetch rule by ID
  const fetchRule = useCallback(async () => {
    if (!id) {
      setError('Rule ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await assignmentRuleService.getRuleById(id);
      
      if (result.success) {
        const ruleData = result.rule;
        setRule(ruleData);
        
        // Extract assignee IDs and load user data
        const assigneeIds = ruleData.assignees.map(a => 
          typeof a === 'object' ? (a._id || a.id) : a
        ).filter(id => id);
        
        // Extract user data from populated assignees
        const assigneeUserData = ruleData.assignees
          .filter(a => typeof a === 'object' && (a._id || a.id))
          .map(a => ({
            id: a._id || a.id,
            name: a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || a._id || a.id,
            email: a.email || '',
            department: a.department || '',
            role: a.role || ''
          }));
        
        if (assigneeUserData.length > 0) {
          setUsers(assigneeUserData);
        }
        
        // Load any missing assignees by IDs
        const unpopulatedIds = assigneeIds.filter(id => 
          !assigneeUserData.find(u => u.id === id)
        );
        if (unpopulatedIds.length > 0) {
          await loadUsersByIds(unpopulatedIds);
        }
      } else {
        setError(result.error || 'Failed to fetch assignment rule');
        toast.error(result.error || 'Failed to fetch assignment rule');
      }
    } catch (error) {
      setError('Failed to fetch assignment rule');
      toast.error('Failed to fetch assignment rule');
    } finally {
      setLoading(false);
    }
  }, [id, loadUsersByIds]);

  useEffect(() => {
    fetchRule();
  }, [fetchRule]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading rule details...</p>
        </div>
      </div>
    );
  }

  if (error || !rule) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/lead_assignment')}
          >
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Rule Details</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error || 'Rule not found'}</p>
            <Button
              variant="primary"
              onClick={() => navigate('/lead_assignment')}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Back to Assignment Rules
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rule Details</h1>
          <p className="text-gray-600">View complete information about the assignment rule</p>
        </div>
      </div>

      {/* Rule Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Rule Name and Status */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-start flex-wrap gap-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900 break-words flex-1 min-w-0 first-letter:capitalize">{rule.name}</h2>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                  rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {rule.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(rule.priority)}`}>
                  {getPriorityLabel(rule.priority)}
                </span>
              </div>
            </div>
            {rule.description && (
              <p className="text-sm text-gray-600 mt-2 break-words whitespace-pre-wrap first-letter:capitalize">{rule.description}</p>
            )}
          </div>

          {/* Conditions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conditions ({rule.conditions?.length || 0})
            </label>
            {rule.conditions && rule.conditions.length > 0 ? (
              <div className="space-y-2">
                {rule.conditions.map((condition, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700 min-w-[100px] flex-shrink-0">{formatFieldName(condition.field)}</span>
                    <span className="text-sm text-gray-500 flex-shrink-0">{formatOperatorName(condition.operator)}</span>
                    <span className="text-sm text-gray-900 flex-1 break-words min-w-0">{formatConditionValue(condition.field, condition.value)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 bg-gray-50 p-3 rounded-lg">No conditions defined</p>
            )}
          </div>

          {/* Assignment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Type
            </label>
            <p className="text-sm text-gray-900 capitalize bg-gray-50 p-3 rounded-lg break-words">
              {rule.assignmentType.replace('_', ' ')}
            </p>
          </div>

          {/* Assignees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignees ({rule.assignees?.length || 0})
            </label>
            {rule.assignees && rule.assignees.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {rule.assignees.map((a, index) => {
                  const assignee = typeof a === 'object' ? a : users.find(u => u.id === a);
                  const assigneeId = typeof a === 'object' ? (a._id || a.id) : a;
                  const assigneeName = assignee 
                    ? (assignee.name || `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim() || assignee.email || assigneeId) 
                    : assigneeId;
                  
                  return (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-gradient-to-br from-agrosaf-green to-agrosaf-blue rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-medium">
                          {assigneeName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate capitalize">{assigneeName}</div>
                        {assignee && assignee.email && (
                          <div className="text-xs text-gray-500 truncate">{assignee.email}</div>
                        )}
                        {assignee && assignee.department && (
                          <div className="text-xs text-gray-500 capitalize">{assignee.department}</div>
                        )}
                        {assignee && assignee.role && (
                          <div className="text-xs text-gray-500 capitalize">{assignee.role}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 bg-gray-50 p-3 rounded-lg">No assignees defined</p>
            )}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                <Clock className="h-4 w-4 inline mr-1" />
                Created Date
              </label>
              <p className="text-sm text-gray-900">
                {formatDate(rule.createdAt)}
              </p>
            </div>
            {rule.lastUsed && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  <Target className="h-4 w-4 inline mr-1" />
                  Last Used
                </label>
                <p className="text-sm text-gray-900">
                  {formatDate(rule.lastUsed)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentRuleDetail;

